import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Field, Stat, Textarea } from "@/components/UI";
import { Plus, Trash, ShieldCheck, Warning, PencilSimple, ChartLine, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const AGE_GROUPS = ["0-3", "3-6", "6-9", "9-12", "12-15", "15-18", "18-21", "21-24", "24+"];
const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const emptyForm = () => ({
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  previous_total: 0, cow_births: 0, heifer_births: 0, total_births: 0, stillbirths: 0,
  purchased: 0, slaughtered_sold: 0, disease_deaths: 0,
  milking: 0, dry: 0, pregnant_heifers: 0, antibiotic_withdrawal: 0,
  female_calf_this_month: 0,
  female_age_groups: Object.fromEntries(AGE_GROUPS.map(g => [g, 0])),
  male_calf_this_month: 0,
  male_age_groups: Object.fromEntries(AGE_GROUPS.map(g => [g, 0])),
  inovasyon_groups: Object.fromEntries(AGE_GROUPS.map(g => [g, 0])),
  notes: "",
});

export default function HerdStatus() {
  const [snaps, setSnaps] = useState([]);
  const [trend, setTrend] = useState([]);
  const [reasons, setReasons] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    const [s, t, r] = await Promise.all([
      api.get("/herd/snapshots"),
      api.get("/herd/trend?months=12"),
      api.get("/herd/leaving-reasons"),
    ]);
    setSnaps(s.data);
    setTrend(t.data);
    setReasons(r.data);
  };
  useEffect(() => { load(); }, []);

  const startNew = async () => {
    const now = new Date();
    let y = now.getFullYear(), m = now.getMonth() + 1;
    // Find next month that doesn't have a snapshot
    while (snaps.find(s => s.year === y && s.month === m)) {
      m++;
      if (m > 12) { m = 1; y++; }
    }
    try {
      const res = await api.get(`/herd/snapshots/suggestion?year=${y}&month=${m}`);
      const s = res.data;
      setForm({
        ...emptyForm(),
        year: s.year, month: s.month,
        previous_total: s.previous_total,
        cow_births: s.cow_births, heifer_births: s.heifer_births,
        total_births: s.total_births, stillbirths: s.stillbirths,
        female_calf_this_month: s.female_calf_this_month,
        male_calf_this_month: s.male_calf_this_month,
        female_age_groups: { ...emptyForm().female_age_groups, ...s.female_age_groups },
        male_age_groups: { ...emptyForm().male_age_groups, ...s.male_age_groups },
      });
      setEditing("new");
      toast.success(`${MONTHS[m - 1]} ${y} önerileri yüklendi. Anaç sürü + inovasyon değerlerini doldurun.`);
    } catch (err) { toast.error(err.message); }
  };

  const startEdit = (snap) => {
    setForm({
      ...emptyForm(),
      ...snap,
      female_age_groups: { ...emptyForm().female_age_groups, ...(snap.female_age_groups || {}) },
      male_age_groups: { ...emptyForm().male_age_groups, ...(snap.male_age_groups || {}) },
      inovasyon_groups: { ...emptyForm().inovasyon_groups, ...(snap.inovasyon_groups || {}) },
    });
    setEditing(snap.id);
  };

  const submit = async (e) => {
    e.preventDefault();
    const numeric = ["year", "month", "previous_total", "cow_births", "heifer_births", "total_births",
      "stillbirths", "purchased", "slaughtered_sold", "disease_deaths",
      "milking", "dry", "pregnant_heifers", "antibiotic_withdrawal",
      "female_calf_this_month", "male_calf_this_month"];
    const body = { ...form };
    numeric.forEach(k => body[k] = Number(body[k] || 0));
    ["female_age_groups", "male_age_groups", "inovasyon_groups"].forEach(k => {
      const obj = body[k] || {};
      body[k] = Object.fromEntries(Object.entries(obj).map(([g, v]) => [g, Number(v || 0)]));
    });
    // Always derive total_births from cow + heifer
    body.total_births = body.cow_births + body.heifer_births;

    try {
      if (editing === "new") {
        await api.post("/herd/snapshots", body);
        toast.success("Sürü kartı oluşturuldu");
      } else {
        await api.patch(`/herd/snapshots/${editing}`, body);
        toast.success("Güncellendi");
      }
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const verify = async (id) => {
    if (!window.confirm("Bu kartı ONAYLAYAN sizsiniz. Onaylandıktan sonra silinemez. Onaylıyor musunuz?")) return;
    try {
      await api.post(`/herd/snapshots/${id}/verify`);
      toast.success("Onaylandı");
      load();
    } catch (err) { toast.error(err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu sürü kartını silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/herd/snapshots/${id}`);
      toast.success("Silindi");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  // Compute live totals from form
  const liveTotals = useMemo(() => computeTotals(form), [form]);

  return (
    <div data-testid="herd-status-page">
      <PageHeader
        title="Sürü Durum Tablosu"
        subtitle="Aylık sürü sayımı — bu ayın devir, doğum, satış ve mevcut anaç sürü kompozisyonu. Doğum kayıtlarından otomatik önerilerle çalışır, sen doldurup müdür onaylar."
        actions={
          <Button data-testid="herd-new-btn" onClick={startNew}>
            <Plus size={14} weight="bold" className="inline mr-1" /> Yeni Ay Kartı
          </Button>
        }
      />

      {trend.length > 1 && (
        <Card className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <ChartLine size={18} weight="duotone" />
            <h3 className="heading text-lg">Son 12 Ay Hayvan Sayısı Trendi</h3>
          </div>
          <div style={{ height: 200 }}>
            <ResponsiveContainer>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8a938c" }} />
                <YAxis tick={{ fontSize: 11, fill: "#8a938c" }} />
                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC" }} />
                <Line type="monotone" dataKey="total" stroke="#2D4A36" strokeWidth={2.5} dot={{ r: 4, fill: "#2D4A36" }} name="Toplam" />
                <Line type="monotone" dataKey="breeding" stroke="#C45B45" strokeWidth={2} dot={{ r: 3 }} name="Anaç" />
                <Line type="monotone" dataKey="female_calves" stroke="#8B6914" strokeWidth={1.5} dot={{ r: 3 }} name="Dişi Düve" />
                <Line type="monotone" dataKey="male_calves" stroke="#6b746c" strokeWidth={1.5} dot={{ r: 3 }} name="Erkek" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {reasons && reasons.total_left > 0 && (
        <LeavingReasonsChart data={reasons} />
      )}

      {editing ? (
        <SnapshotForm
          form={form} setForm={setForm}
          liveTotals={liveTotals}
          onSubmit={submit} onCancel={() => setEditing(null)}
          isNew={editing === "new"}
        />
      ) : (
        <SnapshotList snaps={snaps} onEdit={startEdit} onVerify={verify} onDelete={remove} />
      )}
    </div>
  );
}

// -------- Live totals computation (mirrors backend _calc_totals) --------
function computeTotals(f) {
  const n = v => Number(v || 0);
  const totalBirths = n(f.cow_births) + n(f.heifer_births);
  const ara_toplam = n(f.previous_total) + totalBirths + n(f.purchased);
  const left_herd = n(f.slaughtered_sold) + n(f.disease_deaths) + n(f.stillbirths);
  const final_total = ara_toplam - left_herd;
  const breeding = n(f.milking) + n(f.dry) + n(f.pregnant_heifers) + n(f.antibiotic_withdrawal);
  const female = n(f.female_calf_this_month) + Object.values(f.female_age_groups || {}).reduce((s, v) => s + n(v), 0);
  const male = n(f.male_calf_this_month) + Object.values(f.male_age_groups || {}).reduce((s, v) => s + n(v), 0);
  const inovasyon = Object.values(f.inovasyon_groups || {}).reduce((s, v) => s + n(v), 0);
  const grand_total = breeding + female + male + inovasyon;
  const diff = grand_total - final_total;
  return { ara_toplam, left_herd, final_total, breeding, female, male, inovasyon, grand_total, diff };
}

// -------- Snapshot List --------
function SnapshotList({ snaps, onEdit, onVerify, onDelete }) {
  if (!snaps.length) {
    return (
      <Card className="text-center py-12">
        <Sparkle size={40} weight="duotone" className="mx-auto text-[#8a938c] mb-3" />
        <div className="text-[#6b746c] mb-1">Henüz sürü kartı yok</div>
        <div className="text-xs text-[#8a938c]">"Yeni Ay Kartı" butonuyla başlayın — doğum kayıtları otomatik yüklenir.</div>
      </Card>
    );
  }
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {snaps.map(s => (
        <Card key={s.id} data-testid={`herd-card-${s.id}`}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-xs text-[#6b746c] uppercase tracking-widest">{MONTHS[s.month - 1]} {s.year}</div>
              <h3 className="heading text-2xl">{s.final_total} <span className="text-sm font-normal text-[#6b746c]">toplam hayvan</span></h3>
              {s.verified ? (
                <span className="inline-flex items-center gap-1 text-xs bg-[#E4EEE6] text-[#2D4A36] px-2 py-0.5 rounded mt-1">
                  <ShieldCheck size={12} weight="fill" /> Müdür Onayı Var
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs bg-[#F5E9D3] text-[#8B6914] px-2 py-0.5 rounded mt-1">
                  <Warning size={12} weight="fill" /> Taslak — Onay Bekliyor
                </span>
              )}
            </div>
            <div className="flex gap-1">
              <button onClick={() => onEdit(s)} data-testid={`herd-edit-${s.id}`}
                className="p-1.5 rounded hover:bg-[#F5F2EC]" title="Düzenle">
                <PencilSimple size={16} />
              </button>
              {!s.verified && (
                <>
                  <button onClick={() => onVerify(s.id)} data-testid={`herd-verify-${s.id}`}
                    className="p-1.5 rounded hover:bg-[#E4EEE6] text-[#2D4A36]" title="Müdür Onayla">
                    <ShieldCheck size={16} />
                  </button>
                  <button onClick={() => onDelete(s.id)} data-testid={`herd-del-${s.id}`}
                    className="p-1.5 rounded hover:bg-[#fbeee9] text-[#C45B45]" title="Sil">
                    <Trash size={16} />
                  </button>
                </>
              )}
            </div>
          </div>

          {s.consistency_diff !== 0 && (
            <div className="mb-3 text-xs px-2 py-1.5 rounded bg-[#fbeee9] border border-[#f2d4cb] text-[#8a3a2b]"
              data-testid={`herd-mismatch-${s.id}`}>
              ⚠️ Kategori toplamı ({s.grand_total}) ile toplam hayvan ({s.final_total}) arasında <b>{Math.abs(s.consistency_diff)}</b> hayvan farkı var
            </div>
          )}

          <div className="grid grid-cols-4 gap-2 text-center text-xs">
            <div className="bg-[#F5F2EC] rounded p-2">
              <div className="text-[#6b746c]">Anaç</div>
              <div className="font-bold text-lg tabnum text-[#2D4A36]">{s.breeding_total}</div>
            </div>
            <div className="bg-[#F5F2EC] rounded p-2">
              <div className="text-[#6b746c]">Dişi Düve</div>
              <div className="font-bold text-lg tabnum text-[#C45B45]">{s.female_total}</div>
            </div>
            <div className="bg-[#F5F2EC] rounded p-2">
              <div className="text-[#6b746c]">Erkek</div>
              <div className="font-bold text-lg tabnum text-[#6b746c]">{s.male_total}</div>
            </div>
            <div className="bg-[#F5F2EC] rounded p-2">
              <div className="text-[#6b746c]">İnovasyon</div>
              <div className="font-bold text-lg tabnum text-[#8B6914]">{s.inovasyon_total}</div>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-[#E5E2DC] text-xs text-[#6b746c] grid grid-cols-3 gap-2">
            <div>Devir: <b className="text-[#1A1D1A] tabnum">{s.previous_total}</b></div>
            <div>Doğum: <b className="text-[#2D4A36] tabnum">+{s.total_births}</b></div>
            <div>Çıkan: <b className="text-[#C45B45] tabnum">−{s.left_herd}</b></div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// -------- Snapshot Form --------
function SnapshotForm({ form, setForm, liveTotals, onSubmit, onCancel, isNew }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setGroup = (which, g, v) => setForm(f => ({ ...f, [which]: { ...(f[which] || {}), [g]: v } }));
  const numeric = (v) => v === "" ? "" : Number(v);

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Header block */}
      <Card>
        <div className="flex items-center gap-3 mb-3">
          <h2 className="heading text-2xl">
            {isNew ? "Yeni Sürü Kartı" : "Sürü Kartı Düzenle"}
          </h2>
          <div className="text-[#6b746c]">— {MONTHS[form.month - 1]} {form.year}</div>
        </div>

        {/* Live totals banner */}
        <div className={`text-sm px-4 py-3 rounded-md border grid grid-cols-2 md:grid-cols-5 gap-3 ${
          liveTotals.diff === 0 ? "bg-[#E4EEE6] border-[#c2d6c8]" : "bg-[#fbeee9] border-[#f2d4cb]"
        }`} data-testid="herd-live-totals">
          <div><span className="text-[#6b746c]">Toplam Hayvan:</span> <b className="tabnum">{liveTotals.final_total}</b></div>
          <div><span className="text-[#6b746c]">Anaç:</span> <b className="tabnum">{liveTotals.breeding}</b></div>
          <div><span className="text-[#6b746c]">Dişi Düve:</span> <b className="tabnum">{liveTotals.female}</b></div>
          <div><span className="text-[#6b746c]">Erkek + İnovasyon:</span> <b className="tabnum">{liveTotals.male + liveTotals.inovasyon}</b></div>
          <div className={liveTotals.diff === 0 ? "text-[#2D4A36]" : "text-[#8a3a2b]"}>
            {liveTotals.diff === 0 ? "✓ Toplamlar örtüşüyor" : `⚠️ ${Math.abs(liveTotals.diff)} hayvan farkı`}
          </div>
        </div>
      </Card>

      {/* Section 1: Devir + Doğum + Çıkan */}
      <Card>
        <h3 className="heading text-lg mb-3">Ay Girişi</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Ay *">
            <select data-testid="herd-month" required value={form.month} onChange={e => set("month", Number(e.target.value))}
              className="w-full px-3 py-2 rounded-md border border-[#E5E2DC] bg-white text-[#1A1D1A]">
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </Field>
          <Field label="Yıl *"><Input data-testid="herd-year" type="number" required value={form.year} onChange={e => set("year", numeric(e.target.value))} /></Field>
          <Field label="Devir Eden Hayvan Sayısı 🤖"><Input data-testid="herd-prev-total" type="number" min="0" value={form.previous_total} onChange={e => set("previous_total", numeric(e.target.value))} /></Field>
        </div>
        <div className="grid md:grid-cols-4 gap-3 mt-3">
          <Field label="İnek Doğum 🤖"><Input data-testid="herd-cow-births" type="number" min="0" value={form.cow_births} onChange={e => set("cow_births", numeric(e.target.value))} /></Field>
          <Field label="Düve Doğum 🤖"><Input data-testid="herd-heifer-births" type="number" min="0" value={form.heifer_births} onChange={e => set("heifer_births", numeric(e.target.value))} /></Field>
          <Field label="Toplam Doğum (otomatik)"><Input data-testid="herd-total-births" type="number" readOnly
            value={Number(form.cow_births || 0) + Number(form.heifer_births || 0)}
            className="!bg-[#F5F2EC]" /></Field>
          <Field label="Satın Alınan"><Input data-testid="herd-purchased" type="number" min="0" value={form.purchased} onChange={e => set("purchased", numeric(e.target.value))} /></Field>
        </div>
        <div className="grid md:grid-cols-3 gap-3 mt-3">
          <Field label="Kesilen / Satılan"><Input data-testid="herd-sold" type="number" min="0" value={form.slaughtered_sold} onChange={e => set("slaughtered_sold", numeric(e.target.value))} /></Field>
          <Field label="Mundar Ölen"><Input data-testid="herd-deaths" type="number" min="0" value={form.disease_deaths} onChange={e => set("disease_deaths", numeric(e.target.value))} /></Field>
          <Field label="Ölü Doğum (Abort) 🤖"><Input data-testid="herd-stillbirths" type="number" min="0" value={form.stillbirths} onChange={e => set("stillbirths", numeric(e.target.value))} /></Field>
        </div>

        <div className="mt-4 p-3 bg-[#F5F2EC] rounded text-sm grid grid-cols-2 md:grid-cols-3 gap-2 tabnum">
          <div>Ara Toplam: <b>{liveTotals.ara_toplam}</b></div>
          <div>Sürüden Çıkan: <b>−{liveTotals.left_herd}</b></div>
          <div className="text-[#2D4A36]"><b>TOPLAM HAYVAN: {liveTotals.final_total}</b></div>
        </div>
      </Card>

      {/* Section 2: Anaç Sürü */}
      <Card>
        <h3 className="heading text-lg mb-3">Anaç Güncel Sürü <span className="text-xs text-[#6b746c] font-normal">(sağmal/kuru/gebe düve — elle giriş)</span></h3>
        <div className="grid md:grid-cols-4 gap-3">
          <Field label="Sağmal"><Input data-testid="herd-milking" type="number" min="0" value={form.milking} onChange={e => set("milking", numeric(e.target.value))} /></Field>
          <Field label="Kuru"><Input data-testid="herd-dry" type="number" min="0" value={form.dry} onChange={e => set("dry", numeric(e.target.value))} /></Field>
          <Field label="Gebe Düve"><Input data-testid="herd-preg-heifer" type="number" min="0" value={form.pregnant_heifers} onChange={e => set("pregnant_heifers", numeric(e.target.value))} /></Field>
          <Field label="Antb. / Ağızlı Kuru Çık."><Input data-testid="herd-antb" type="number" min="0" value={form.antibiotic_withdrawal} onChange={e => set("antibiotic_withdrawal", numeric(e.target.value))} /></Field>
        </div>
        <div className="mt-3 text-sm text-right">Toplam Anaç: <b className="tabnum text-[#2D4A36]">{liveTotals.breeding}</b></div>
      </Card>

      {/* Section 3: Dişi Düve Yaş Grupları */}
      <AgeGroupBlock
        title="Dişi Düve Hayvan Grubu"
        subtitle="Doğum kayıtlarından öneri geldi — elle düzeltebilirsin (düve→inek geçişleri için)"
        thisMonthKey="female_calf_this_month" thisMonthLabel={`${form.year} ${MONTHS[form.month - 1]} Doğ. Dişi Buz. 🤖`}
        groupsKey="female_age_groups" testPrefix="herd-female"
        form={form} set={set} setGroup={setGroup} total={liveTotals.female} tone="warn"
      />

      {/* Section 4: Erkek Yaş Grupları */}
      <AgeGroupBlock
        title="Erkek Hayvan Grubu"
        subtitle="Doğum kayıtlarından öneri geldi — elle düzeltebilirsin"
        thisMonthKey="male_calf_this_month" thisMonthLabel={`${form.year} ${MONTHS[form.month - 1]} Doğ. Erkek Buz. 🤖`}
        groupsKey="male_age_groups" testPrefix="herd-male"
        form={form} set={set} setGroup={setGroup} total={liveTotals.male} tone="neutral"
      />

      {/* Section 5: İnovasyon (Besi) */}
      <Card>
        <h3 className="heading text-lg mb-3">İnovasyon (Besi Grubu) <span className="text-xs text-[#6b746c] font-normal">(opsiyonel — ayrı bir kategori olarak besi hayvanları)</span></h3>
        <div className="grid grid-cols-3 md:grid-cols-9 gap-2">
          {AGE_GROUPS.map(g => (
            <Field key={g} label={`${g} ay`}>
              <Input data-testid={`herd-inovasyon-${g}`} type="number" min="0"
                value={form.inovasyon_groups[g] || 0}
                onChange={e => setGroup("inovasyon_groups", g, numeric(e.target.value))} />
            </Field>
          ))}
        </div>
        <div className="mt-3 text-sm text-right">Toplam İnovasyon: <b className="tabnum text-[#8B6914]">{liveTotals.inovasyon}</b></div>
      </Card>

      {/* Notes + Submit */}
      <Card>
        <Field label="Notlar">
          <Textarea rows={2} data-testid="herd-notes" value={form.notes} onChange={e => set("notes", e.target.value)} />
        </Field>
        <div className="mt-4 flex gap-2 justify-end">
          <Button type="button" onClick={onCancel} className="!bg-transparent !text-[#6b746c] border border-[#E5E2DC]">İptal</Button>
          <Button type="submit" data-testid="herd-save"><Plus size={14} weight="bold" className="inline mr-1" /> {isNew ? "Kartı Oluştur" : "Kaydet"}</Button>
        </div>
        {liveTotals.diff !== 0 && (
          <div className="mt-3 text-xs px-3 py-2 rounded bg-[#F5E9D3] border border-[#e0c890] text-[#8B6914]">
            ⚠️ Kategori toplamı ({liveTotals.grand_total}) ile toplam hayvan sayısı ({liveTotals.final_total}) örtüşmüyor. Kart kaydedilecek ancak müdür onayında bu farka bakılmalı.
          </div>
        )}
      </Card>
    </form>
  );
}

function AgeGroupBlock({ title, subtitle, thisMonthKey, thisMonthLabel, groupsKey, testPrefix, form, set, setGroup, total, tone }) {
  const numeric = (v) => v === "" ? "" : Number(v);
  const toneColor = tone === "warn" ? "text-[#C45B45]" : "text-[#6b746c]";
  return (
    <Card>
      <div className="mb-3">
        <h3 className="heading text-lg">{title}</h3>
        <div className="text-xs text-[#6b746c]">{subtitle}</div>
      </div>
      <Field label={thisMonthLabel}>
        <Input data-testid={`${testPrefix}-this-month`} type="number" min="0"
          value={form[thisMonthKey]} onChange={e => set(thisMonthKey, numeric(e.target.value))} />
      </Field>
      <div className="grid grid-cols-3 md:grid-cols-9 gap-2 mt-3">
        {AGE_GROUPS.map(g => (
          <Field key={g} label={`${g} ay`}>
            <Input data-testid={`${testPrefix}-${g}`} type="number" min="0"
              value={form[groupsKey][g] || 0}
              onChange={e => setGroup(groupsKey, g, numeric(e.target.value))} />
          </Field>
        ))}
      </div>
      <div className={`mt-3 text-sm text-right ${toneColor}`}>Toplam: <b className="tabnum">{total}</b></div>
    </Card>
  );
}

// -------- Leaving Reasons Pie Chart --------
const REASON_COLORS = { "Kesilen/Satılan": "#2D4A36", "Mundar Ölen": "#C45B45", "Ölü Doğum (Abort)": "#8B6914" };

function LeavingReasonsChart({ data }) {
  const chartData = data.breakdown.filter(x => x.count > 0);
  const worst = [...data.breakdown].sort((a, b) => b.count - a.count)[0];
  return (
    <Card className="mb-6" data-testid="herd-leaving-reasons">
      <div className="flex items-center gap-2 mb-3">
        <Sparkle size={18} weight="duotone" />
        <h3 className="heading text-lg">Sürüden Ayrılma Sebepleri</h3>
        <span className="text-xs text-[#6b746c] ml-2">({data.months_covered} aylık toplu veri · {data.total_left} hayvan)</span>
      </div>
      <div className="grid md:grid-cols-2 gap-6 items-center">
        <div style={{ height: 220 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={chartData} dataKey="count" nameKey="reason" innerRadius={55} outerRadius={90} paddingAngle={2}>
                {chartData.map((x, i) => <Cell key={x.reason} fill={REASON_COLORS[x.reason] || "#6b746c"} />)}
              </Pie>
              <Tooltip
                formatter={(val, name) => [`${val} hayvan`, name]}
                contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC", fontSize: 12 }}
              />
              <Legend verticalAlign="bottom" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-2">
          {data.breakdown.map(x => (
            <div key={x.reason} data-testid={`reason-${x.reason}`}
                 className="flex items-center justify-between py-2 px-3 bg-[#F5F2EC] rounded">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm" style={{ background: REASON_COLORS[x.reason] || "#6b746c" }} />
                <span className="text-sm font-semibold">{x.reason}</span>
              </div>
              <div className="text-right">
                <div className="tabnum font-bold">{x.count} <span className="text-xs text-[#6b746c] font-normal">baş</span></div>
                <div className="text-xs text-[#6b746c] tabnum">
                  ayrılanların %{x.pct_of_leaving} · sürünün %{x.pct_of_herd}
                </div>
              </div>
            </div>
          ))}
          {worst && worst.count > 0 && (
            <div className="text-xs px-3 py-2 mt-3 rounded bg-[#fbeee9] border border-[#f2d4cb] text-[#8a3a2b]">
              🔍 En yüksek kayıp: <b>{worst.reason}</b> (%{worst.pct_of_leaving}) — bu kategoriye özel önlem alınabilir.
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

